import Link from 'next/link'
import PlusCircle from 'public/icons/interface/plus-circle.svg'

const LINK_VARIANTS = ['link-light', 'link-dark']

export default function Button({
  size = 'm',
  icon = 'none',
  variant = 'light',
  iconComponent: IconComponent = PlusCircle,
  children,
  className = '',
  contentClassName = '',
  textClassName = '',
  disabled = false,
  href,
  onFocus,
  onBlur,
  onClick,
  ...props
}) {
  const isLinkVariant = LINK_VARIANTS.includes(variant)

  // Build utility class name - no circular references in Tailwind v4
  let buttonClasses = 'btn-base'

  // Add size utility
  buttonClasses += ` btn-${size}`

  // Add icon positioning utility if present
  if (icon !== 'none') {
    buttonClasses += ` btn-${size}-icon-${icon}`
  } else if (size === 's') {
    buttonClasses += ' btn-s-no-icon'
  }

  // Add variant utility
  if (!disabled) {
    if (isLinkVariant) {
      buttonClasses += ' btn-link'
      buttonClasses +=
        variant === 'link-dark' ? ' btn-link-dark btn-link-dark-hover' : ' btn-link-light btn-link-light-hover'
      buttonClasses += ' btn-link-focus'
    } else {
      buttonClasses += ` btn-${variant}`
      if (variant === 'dark') {
        buttonClasses += ' btn-dark-hover'
      } else {
        buttonClasses += ' btn-hover'
      }
      buttonClasses += ' btn-focus'
    }
  }

  // Add typography utility
  buttonClasses += ` btn-text-${size}`

  // Add disabled state with inline utilities (since states are dynamic)
  if (disabled) {
    let disabledClasses = 'cursor-not-allowed'
    if (isLinkVariant) {
      disabledClasses += ' text-basic-gray3'
    } else {
      disabledClasses +=
        variant === 'dark'
          ? ' bg-blueish-blueish1 border-dark-blue-dark-blue4 text-dark-blue-dark-blue4'
          : ' bg-basic-gray8 border-basic-gray3 text-basic-gray3'
    }
    buttonClasses += ` ${disabledClasses}`
  }

  const combinedClassName = `${buttonClasses} ${className}`.trim()

  const renderIcon = () => {
    if (icon === 'none') return null

    return IconComponent && IconComponent != PlusCircle ? (
      <IconComponent />
    ) : (
      <PlusCircle className="transition-transform group-hover:rotate-180 duration-300 ease-out" />
    )
  }

  const content = (
    <>
      {icon === 'left' && renderIcon()}
      <div className={`btn__content-wrapper ${contentClassName}`.trim()}>
        <span className={`btn__text ${textClassName}`.trim()}>{children}</span>
      </div>
      {icon === 'right' && renderIcon()}
    </>
  )

  if (href) {
    if (disabled) {
      return (
        <span className={`${combinedClassName} group`} role="link" aria-disabled tabIndex={-1} {...props}>
          {content}
        </span>
      )
    }

    return (
      <Link href={href} className={`${combinedClassName} group`} {...props}>
        {content}
      </Link>
    )
  }

  return (
    <button
      className={`${combinedClassName} group`}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      {...props}
    >
      {content}
    </button>
  )
}
